import type { PerformancePart } from '@tunasong/schemas'
import { createContext, useContext } from 'react'

/**
 * What the performer's parts need on screen: the UNION of what each chosen part needs (spec "Combining
 * parts"). Editor plugins read these flags, never the parts themselves, so a new part or a rule change
 * lives in `partFeatures` alone. Plugins change only how things render, never which nodes exist (D3).
 */
export interface PartFeatures {
  /** Guitar, Bass, Keys or Full chart chosen, or "Show chords" on. */
  chordsShown: boolean
  /** Drums with chords hidden: a no-chord (N.C.) renders as STOP. */
  stopForNoChord: boolean
  /** Vocals: lyrics larger, chords kept on their syllable. */
  largeLyrics: boolean
  /** Vocals: backing lines, cues and dynamics at full contrast (cues are never hidden). */
  vocalCues: boolean
  /** Drums without Vocals: lyrics dimmed, never hidden. */
  dimLyrics: boolean
  /** Drums or Bass: bar counts, odd-meter badges, hits and stops. */
  structure: boolean
  /** Drums: the tempo segment of the header line (the metronome already shows tempo to others, D15). */
  tempoLine: boolean
  /** Drums or Vocals: the count-in segment of the header line. */
  countIn: boolean
  /**
   * No chosen part shows chords (D16): instrumental sections collapse to one line with their bar count,
   * which is what a singer or a drummer needs instead of blank chord-only lines.
   */
  collapseInstrumental: boolean
  /** Guitar: tabs expanded. */
  tabsExpanded: boolean
}

const chordParts: readonly PerformancePart[] = ['guitar', 'bass', 'keys', 'full']

export const partFeatures = (parts: readonly PerformancePart[], options: { showChords: boolean }): PartFeatures => {
  const has = (part: PerformancePart) => parts.includes(part)
  const vocals = has('vocals')
  const drums = has('drums')
  const chordsShown = parts.some(part => chordParts.includes(part)) || options.showChords
  return {
    chordsShown,
    stopForNoChord: drums && !chordsShown,
    largeLyrics: vocals,
    vocalCues: vocals,
    dimLyrics: drums && !vocals,
    structure: drums || has('bass'),
    tempoLine: drums,
    countIn: drums || vocals,
    collapseInstrumental: !chordsShown,
    tabsExpanded: has('guitar'),
  }
}

export type NotesPlacement = 'beside' | 'panel'

/** A request to write a note (N, "+ Jot"). `sectionId: null` writes a song note, anchored to nothing. */
export interface NoteComposeRequest {
  sectionId: string | null
  /** `inline` opens the composer beside the section; `sheet` opens the Jot sheet. */
  surface: 'inline' | 'sheet'
}

/** The performer's own notes in Perform. Private, never tagged by part. */
export interface PerformanceNotesView {
  visible: boolean
  /** Where the notes render. `beside` is the margin on wide screens and inline under each section label on phones. */
  placement: NotesPlacement
  compose: NoteComposeRequest | null
  clearCompose: () => void
  /** Above the song title: where song notes (unanchored or orphaned) and the margin heading render. */
  songNotesSlot: HTMLElement | null
}

export interface PerformanceViewValue {
  parts: PerformancePart[]
  features: PartFeatures
  notes: PerformanceNotesView
}

/** Set by the performance views around the song; `null` everywhere else (the editor, previews). */
export const PerformanceViewContext = createContext<PerformanceViewValue | null>(null)

export const usePerformanceView = () => useContext(PerformanceViewContext)
